import { useEffect, useState } from "react";
import { Plus } from "@phosphor-icons/react";
import { AddOnMetaType } from "lib/types/checkout";
import { truncateString } from "lib/utils/helpers";
import defaultPackageImage from "assests/images/package.png";
import { currencyFormatter } from "lib/utils/formatters";
import { Icon } from "@shopflo/ui/icon";
import CircularSpinner from "components/common/loaders/CircularSpinner";
import { AddOnConfigType } from "lib/types/merchantConfigTypes";
import { getStyleFromCSSVariable } from "@shopflo/ui/utils";
import { useLocale } from "lib/hooks/useLocale";

export const AddOnButton = ({
  addOnData,
  handleClick,
  apiActionLoading = false,
  customisations,
}: {
  addOnData: AddOnMetaType;
  handleClick?: () => void;
  apiActionLoading?: boolean;
  customisations?: AddOnConfigType;
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const { t } = useLocale();

  useEffect(() => {
    if (!apiActionLoading) {
      setIsLoading(false);
    }
  }, [apiActionLoading]);

  const handleAddClick = () => {
    if (isLoading) {
      return;
    }
    setIsLoading(true);
    handleClick?.();
  };

  const iconStyle = getStyleFromCSSVariable('--flo-primary-dark-color', 'fill')
  const spinnerStyle = getStyleFromCSSVariable('--flo-primary-dark-color', 'color')

  return (
    <div className={`flex items-center gap-2.5 rounded-lg px-5 py-1`} onClick={handleAddClick}>
      <div className="flex h-9 w-9 items-center justify-center rounded-md">
        <img
          src={addOnData?.image ?? defaultPackageImage}
          alt={addOnData?.product_name ?? "add-on-image"}
          className="h-full w-full rounded-md object-cover"
          onError={({ currentTarget }) => {
            currentTarget.onerror = null;
            currentTarget.src = defaultPackageImage;
          }}
        />
      </div>

      {/* Content Section */}
      <div className="flex flex-1 flex-col gap-0">
        <div className="flex items-center gap-1">
          <span className="text-text-xs-semibold text-zinc-700 line-clamp-1"
            style={{
              color: customisations?.textColor,
            }}
          >
            {truncateString(addOnData?.product_name ?? "Box Packaging", 40)}
          </span>
        </div>
        <span className="text-text-xs-medium text-zinc-500"
          style={{
            color: customisations?.textColor,
            opacity: 0.6,
          }}
        >
          {addOnData?.price ? currencyFormatter(addOnData.price.toString()) : t("automatic_freebies_free_text")}
        </span>
      </div>

      {isLoading ? (
        <CircularSpinner
          height="h-5"
          width="w-5"
          style={{
            color: customisations?.buttonColor,
            ...(!customisations?.buttonColor ? spinnerStyle : undefined),
          }}
        />
      ) : (
        <button className="flex items-center gap-0.5 rounded-[5.6px] px-1 py-0.5">
          <div className="flex h-3 w-3 items-center justify-center rounded-[5.6px]"
          >
            <Icon
              IconComponent={Plus}
              size="xxs"
              iconProps={{
                weight: "bold",
                color: customisations?.buttonColor,
                style: !customisations?.buttonColor ? iconStyle : undefined
              }}
            />
          </div>
          <span className="text-text-sm-semibold text-primary-dark"
            style={{
              color: customisations?.buttonColor,
              ...iconStyle
            }}
          >Add</span>
        </button>
      )}
    </div>
  );
};
